import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { FormikProps, FormikProvider, useFormik, useFormikContext } from 'formik';
import useDeclarations from 'hooks/useDeclarations';
import { useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Declaration } from 'store/declarations/declaration';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import ICS2ENSMasterForm from 'views/declarations/eu/ics2/ICS2ENSMasterForm';
import IrelandEnsForm from 'views/declarations/ireland/ens/IrelandEnsForm';
import IrelandEtdForm from 'views/declarations/ireland/etd/IrelandEtdForm';
import IrelandExportForm from 'views/declarations/ireland/export/IrelandExportForm';
import IrelandH1Form from 'views/declarations/ireland/import/h1/IrelandH1Form';
import IrelandH7Form from 'views/declarations/ireland/import/h7/IrelandH7Form';
import IrelandI1Form from 'views/declarations/ireland/import/i1/IrelandI1Form';
import IrelandNctsForm from 'views/declarations/ireland/ncts/IrelandNctsForm';
import PbnForm from 'views/declarations/ireland/pbn/PbnForm';
import IrelandTsdForm from 'views/declarations/ireland/tsd/IrelandTsdForm';
import UkGvmsForm from 'views/declarations/uk/gvms/UkGvmsForm';
import UkNctsForm from 'views/declarations/uk/ncts/UkNctsForm';
import UkForm from 'views/declarations/uk/UkForm';

const useMasterDetails = () =>
    useOutletContext<{
        formik?: FormikProps<any>;
        viewOnly?: boolean;
        toggleHelp?: (refNumber: string | number) => void;
        selectCard?: (id: string) => void;
    }>();

interface Props {
    declaration?: Declaration | null;
}

const MasterDetails = ({ declaration: declarationProp }: Props) => {
    const context = useMasterDetails();
    const contextFormik = useFormikContext();

    const mockFormik = useFormik({
        initialValues: {},
        onSubmit: () => {},
    });

    const { template } = useTemplateContext();
    const { setForm } = useDeclarationContext();
    useEffect(() => {
        if (template) return;
        setForm?.('master');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { declaration: _declaration } = useDeclarations();
    const declaration = useMemo(() => {
        return declarationProp ?? _declaration;
    }, [declarationProp, _declaration]);

    const formik = useMemo(
        () => contextFormik || context?.formik || mockFormik,
        [context?.formik, contextFormik, mockFormik]
    );

    const container = useMemo(() => {
        if (!declaration || !formik) return null;

        switch (declaration.declarationExternalEntity) {
            case DeclarationExternalEntity.REVENUE: {
                if (DeclarationInternalType.IMPORT === declaration.declarationInternalType) {
                    if (declaration.irelandImportDeclaration) {
                        if (declaration.irelandImportDeclaration.messageType === 'I1') {
                            return (
                                <IrelandI1Form
                                    formik={formik}
                                    toggleHelp={context?.toggleHelp}
                                    viewOnly={context?.viewOnly}
                                    selectCard={context?.selectCard}
                                ></IrelandI1Form>
                            );
                        } else {
                            return (
                                <IrelandH1Form
                                    formik={formik}
                                    toggleHelp={context?.toggleHelp}
                                    viewOnly={context?.viewOnly}
                                    selectCard={context?.selectCard}
                                />
                            );
                        }
                    } else {
                        return (
                            <IrelandH7Form
                                formik={formik}
                                toggleHelp={context?.toggleHelp}
                                viewOnly={context?.viewOnly}
                            />
                        );
                    }
                } else if (
                    DeclarationInternalType.EXPORT === declaration.declarationInternalType ||
                    DeclarationInternalType.ARRIVAL === declaration.declarationInternalType
                ) {
                    return <IrelandExportForm formik={formik} />;
                } else if (DeclarationInternalType.ENS === declaration.declarationInternalType) {
                    return (
                        <IrelandEnsForm formik={formik} toggleHelp={context?.toggleHelp} viewOnly={context?.viewOnly} />
                    );
                } else if (DeclarationInternalType.TEMPORARY === declaration.declarationInternalType) {
                    return <IrelandTsdForm formik={formik} />;
                } else if (DeclarationInternalType.PBN === declaration.declarationInternalType) {
                    return <PbnForm formik={formik} />;
                } else if (DeclarationInternalType.NCTS === declaration.declarationInternalType) {
                    return <IrelandNctsForm formik={formik} customerId={declaration.individual?.customerId} />;
                } else if (DeclarationInternalType.ETD === declaration.declarationInternalType) {
                    return <IrelandEtdForm formik={formik} />;
                } else return <></>;
            }
            case DeclarationExternalEntity.CDS:
                return <UkForm formik={formik} toggleHelp={context?.toggleHelp} viewOnly={context?.viewOnly} />;

            case DeclarationExternalEntity.GVMS:
                return <UkGvmsForm formik={formik} />;

            case DeclarationExternalEntity.HMRC:
                return <UkNctsForm formik={formik} customerId={declaration.individual?.customerId} />;

            case DeclarationExternalEntity.ICS2:
                return (
                    <ICS2ENSMasterForm
                        formik={formik}
                        customerId={declaration.individual?.customerId}
                        viewOnly={context?.viewOnly}
                    />
                );

            default:
                return null;
        }
    }, [declaration, formik, context?.toggleHelp, context?.viewOnly, context?.selectCard]);

    if (!formik) return null;

    return <FormikProvider value={formik}>{container}</FormikProvider>;
};
export default MasterDetails;
